<template>
  <div class="resume-editor">
    <div class="editor-toolbar">
      <div class="toolbar-left">
        <button @click="toggleViewMode" class="toolbar-btn" :title="viewMode === 'edit' ? $t('editor.switchToPreview') : $t('editor.switchToEdit')">
          {{ viewMode === 'edit' ? '👁️ ' + $t('editor.previewBtn') : '✏️ ' + $t('editor.editBtn') }}
        </button>
        <button @click="formatText('bold')" class="toolbar-btn" :title="$t('editor.bold')">
          <strong>B</strong>
        </button>
        <button @click="formatText('italic')" class="toolbar-btn" :title="$t('editor.italic')">
          <em>I</em>
        </button>
        <button @click="formatText('heading')" class="toolbar-btn" :title="$t('editor.heading')">
          H
        </button>
        <button @click="formatText('list')" class="toolbar-btn" :title="$t('editor.bulletList')">
          • List
        </button>
      </div>
      
      <div class="toolbar-right">
        <div class="word-count">
          {{ wordCount }} {{ $t('editor.words') }}
        </div>
        <button @click="addComment" class="toolbar-btn btn-comment" :disabled="!selectedText">
          💬 {{ $t('editor.addComment') }}
        </button>
      </div>
    </div>
    
    <div class="editor-content">
      <div v-if="viewMode === 'edit'" class="markdown-editor">
        <textarea
          ref="textarea"
          v-model="localMarkdown"
          @input="handleInput"
          @select="handleTextSelect"
          :placeholder="$t('editor.startEditingPlaceholder')"
          class="editor-textarea"
        ></textarea>
      </div>
      
      <div v-else class="markdown-preview">
        <div class="preview-content" v-html="renderedMarkdown"></div>
      </div>
    </div>
    
    <div v-if="selectedText" class="selection-info">
      <div class="selection-text">
        <strong>{{ $t('editor.selected') }}</strong> "{{ selectedText.substring(0, 50) }}{{ selectedText.length > 50 ? '...' : '' }}"
      </div>
      <button @click="clearSelection" class="clear-selection-btn" :title="$t('editor.clearSelection')">
        ×
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue'
import { marked } from 'marked'
import { toast } from 'vue3-toastify'

export default {
  name: 'ResumeEditor',
  props: {
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'text-selected', 'add-comment'],
  setup(props, { emit }) {
    const textarea = ref(null)
    const viewMode = ref('edit')
    const localMarkdown = ref(props.modelValue)
    const selectedText = ref('')
    const selectionStart = ref(0)
    const selectionEnd = ref(0)

    // Watch for external changes to modelValue
    watch(() => props.modelValue, (newValue) => {
      if (newValue !== localMarkdown.value) {
        localMarkdown.value = newValue
      }
    })

    // Emit updates when localMarkdown changes
    watch(localMarkdown, (newValue) => {
      emit('update:modelValue', newValue)
    })

    const wordCount = computed(() => {
      return localMarkdown.value.trim().split(/\s+/).filter(word => word.length > 0).length
    })

    const renderedMarkdown = computed(() => {
      return marked(localMarkdown.value || '', {
        breaks: true,
        gfm: true
      })
    })

    const handleInput = () => {
      // Input handling is already covered by the watch on localMarkdown
    }

    const handleTextSelect = () => {
      if (!textarea.value) return
      
      const start = textarea.value.selectionStart
      const end = textarea.value.selectionEnd
      
      if (start !== end) {
        selectedText.value = localMarkdown.value.substring(start, end)
        selectionStart.value = start
        selectionEnd.value = end
        emit('text-selected', selectedText.value)
      } else {
        selectedText.value = ''
        selectionStart.value = 0
        selectionEnd.value = 0
        emit('text-selected', '')
      }
    }

    const toggleViewMode = () => {
      viewMode.value = viewMode.value === 'edit' ? 'preview' : 'edit'
    }

    const formatText = (type) => {
      if (!textarea.value) return
      
      const textareaEl = textarea.value
      const start = textareaEl.selectionStart
      const end = textareaEl.selectionEnd
      
      if (start === end && type !== 'list') {
        toast.warning('Please select some text first')
        return
      }
      
      let formattedText = ''
      let newCursorPos = end
      
      switch (type) {
        case 'bold':
          formattedText = `**${selectedText.value}**`
          newCursorPos = start + formattedText.length
          break
        case 'italic':
          formattedText = `*${selectedText.value}*`
          newCursorPos = start + formattedText.length
          break
        case 'heading':
          formattedText = `## ${selectedText.value}`
          newCursorPos = start + formattedText.length
          break
        case 'list':
          // Add bullet point at current line
          const lines = localMarkdown.value.split('\n')
          const currentLineIndex = localMarkdown.value.substring(0, start).split('\n').length - 1
          lines[currentLineIndex] = lines[currentLineIndex].trim() === '' ? '- ' : `- ${lines[currentLineIndex]}`
          localMarkdown.value = lines.join('\n')
          
          // Set cursor after bullet
          nextTick(() => {
            textareaEl.focus()
            const newPos = start + 2
            textareaEl.setSelectionRange(newPos, newPos)
          })
          return
      }
      
      // Replace selected text with formatted version
      const newMarkdown = localMarkdown.value.substring(0, start) + 
                         formattedText + 
                         localMarkdown.value.substring(end)
      
      localMarkdown.value = newMarkdown
      
      // Set cursor position after formatting
      nextTick(() => {
        textareaEl.focus()
        textareaEl.setSelectionRange(newCursorPos, newCursorPos)
        handleTextSelect()
      })
    }

    const addComment = () => {
      if (!selectedText.value) {
        toast.warning('Please select some text first')
        return
      }
      
      emit('add-comment', selectedText.value)
      clearSelection()
    }

    const clearSelection = () => {
      selectedText.value = ''
      selectionStart.value = 0
      selectionEnd.value = 0
      emit('text-selected', '')
      
      if (textarea.value) {
        textarea.value.focus()
      }
    }

    return {
      textarea,
      viewMode,
      localMarkdown,
      selectedText,
      wordCount,
      renderedMarkdown,
      handleInput,
      handleTextSelect,
      toggleViewMode,
      formatText,
      addComment,
      clearSelection
    }
  }
}
</script>

<style scoped>
.resume-editor {
  display: flex;
  flex-direction: column;
  height: 600px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

.editor-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.toolbar-left, .toolbar-right {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.toolbar-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  background: white;
  color: #4a5568;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.toolbar-btn:hover:not(:disabled) {
  background: #edf2f7;
  border-color: #a0aec0;
}

.toolbar-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-comment {
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: white;
  border: none;
}

.btn-comment:hover:not(:disabled) {
  background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
}

.word-count {
  font-size: 0.85rem;
  color: #718096;
  padding: 0.25rem 0.5rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.editor-content {
  flex: 1;
  overflow: hidden;
}

.markdown-editor, .markdown-preview {
  height: 100%;
  overflow: auto;
}

.editor-textarea {
  width: 100%;
  height: 100%;
  padding: 1rem;
  border: none;
  resize: none;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 14px;
  line-height: 1.6;
  color: #2d3748;
  background: white;
}

.editor-textarea:focus {
  outline: none;
}

.editor-textarea::placeholder {
  color: #a0aec0;
}

.preview-content {
  padding: 1rem;
  height: 100%;
  overflow: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
  color: #2d3748;
}

.preview-content h1, .preview-content h2, .preview-content h3 {
  color: #2d3748;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.preview-content h1 {
  font-size: 1.8rem;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0.5rem;
}

.preview-content h2 {
  font-size: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.25rem;
}

.preview-content h3 {
  font-size: 1.2rem;
}

.preview-content p {
  margin-bottom: 1rem;
}

.preview-content ul, .preview-content ol {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

.preview-content li {
  margin-bottom: 0.25rem;
}

.preview-content strong {
  font-weight: 600;
  color: #2d3748;
}

.preview-content em {
  font-style: italic;
  color: #4a5568;
}

.selection-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #ebf8ff;
  border-top: 1px solid #bee3f8;
  font-size: 0.9rem;
  color: #2c5282;
}

.selection-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clear-selection-btn {
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.25rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-selection-btn:hover {
  background: #e2e8f0;
}
</style>
